import * as React from "react";

function Pagination({
  currentPage = 1,
  totalPages,
  totalItems,
  itemsPerPage,
  siblingsCount = 1,
  onPageChange,
  showFirstLast = true,
  showPrevNext = true,
  showItemsInfo = true,
  disabledClassName = "opacity-50 cursor-not-allowed",
}) {
  if (totalItems === 0) {
    return (
      <div className="w-100 new_pagination relative flex items-center justify-end">
        {showItemsInfo && (
          <p className="total_items absolute left-0 text-[#444]">0 items</p>
        )}
      </div>
    );
  }

  const generatePagination = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingsCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingsCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingsCount;
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, "...", totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingsCount;
      const rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1,
      );
      return [firstPageIndex, "...", ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i,
      );
      return [firstPageIndex, "...", ...middleRange, "...", lastPageIndex];
    }

    return [];
  };

  const pages = generatePagination();

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleSkipBackward = () => {
    const targetPage = Math.max(currentPage - 5, 1);
    onPageChange(targetPage);
  };

  const handleSkipForward = () => {
    const targetPage = Math.min(currentPage + 5, totalPages);
    onPageChange(targetPage);
  };

  return (
    <div
      className={`w-100 new_pagination relative flex items-center justify-center`}
    >
      {showItemsInfo && (
        <p className="total_items absolute left-0 text-[#444]">
          <span className="none">showing</span>
          <span className="fetching_items_info">
            {startItem}-{endItem}
          </span>{" "}
          of <span className="fetching_items_info">{totalItems}</span> items
        </p>
      )}
      <div className="pagination-btns-wrapper flex items-center gap-[12px]">
        {showFirstLast && (
          <button
            className={` ${currentPage <= 5 ? disabledClassName : ""}`}
            onClick={handleSkipBackward}
            disabled={currentPage <= 5}
            aria-label="Skip 5 pages backward"
          >
            <svg
              width={21}
              height={20}
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.0729 14.1673L5.90625 10.0007L10.0729 5.83398"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.9089 14.1673L11.7422 10.0007L15.9089 5.83398"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {showPrevNext && (
          <button
            className={` ${currentPage === 1 ? disabledClassName : ""}`}
            onClick={() => currentPage !== 1 && onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <svg
              width={21}
              height={20}
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.4062 15L8.40625 10L13.4062 5"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {pages?.map((page, index) => (
          <React.Fragment key={index}>
            {page === "..." ? (
              <span className="px-2 py-1 text-[#444]">...</span>
            ) : (
              <button
                className={` ${page === currentPage ? "active_page" : ""}`}
                onClick={() => onPageChange(page)}
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}

        {showPrevNext && (
          <button
            className={` ${currentPage === totalPages ? disabledClassName : ""}`}
            onClick={() =>
              currentPage !== totalPages && onPageChange(currentPage + 1)
            }
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <svg
              width={21}
              height={20}
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.40625 15L13.4062 10L8.40625 5"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {showFirstLast && (
          <button
            className={` ${currentPage >= totalPages - 4 ? disabledClassName : ""}`}
            onClick={handleSkipForward}
            disabled={currentPage >= totalPages - 4}
            aria-label="Skip 5 pages forward"
          >
            <svg
              width={21}
              height={20}
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.7422 14.1673L15.9089 10.0007L11.7422 5.83398"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.90625 14.1673L10.0729 10.0007L5.90625 5.83398"
                stroke="#444444"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default Pagination;
